/**
 * Centralized impersonation session management using localStorage
 * This is the single source of truth for all impersonation-related functionality
 */

import { User } from '../contexts/AuthContext';

// Constants
const IMPERSONATION_STORAGE_KEY = 'kolega_impersonation_session';
const IMPERSONATION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

// Types
export interface StoredImpersonationSession {
  targetUser: User;
  originalAdminUser: User;
  expiresAt: number;
  startedAt: number;
}

export interface ImpersonationState {
  isImpersonating: boolean;
  targetUser?: User;
  originalAdminUser?: User;
  expiresAt?: number;
}

/**
 * Centralized impersonation manager class
 */
export class ImpersonationManager {
  /**
   * Get stored impersonation session from localStorage
   * Automatically removes expired sessions
   */
  static getStoredSession(): StoredImpersonationSession | null {
    try {
      const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (!stored) return null;
      
      const session: StoredImpersonationSession = JSON.parse(stored);
      
      // Check if session has expired
      if (Date.now() > session.expiresAt) {
        console.log('Impersonation session expired, removing from localStorage');
        this.clearSession();
        return null;
      }
      
      return session;
    } catch (error) {
      console.error('Error reading impersonation session from localStorage:', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Store impersonation session to localStorage
   */
  static setStoredSession(session: StoredImpersonationSession): void {
    try {
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Error storing impersonation session to localStorage:', error);
    }
  }

  /**
   * Clear impersonation session from localStorage
   */
  static clearSession(): void {
    try {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing impersonation session from localStorage:', error);
    }
  }

  /**
   * Check if there's an active impersonation session
   */
  static hasActiveSession(): boolean {
    return this.getStoredSession() !== null;
  }

  /**
   * Get remaining time in milliseconds for current session
   */
  static getRemainingTime(): number {
    const session = this.getStoredSession();
    if (!session) return 0;
    return Math.max(0, session.expiresAt - Date.now());
  }

  /**
   * Get expiry date for current session
   */
  static getExpiryTime(): Date | null {
    const session = this.getStoredSession();
    if (!session) return null;
    return new Date(session.expiresAt);
  }

  /**
   * Create a new impersonation session
   */
  static createSession(targetUser: User, originalAdminUser: User): StoredImpersonationSession {
    const now = Date.now();
    const session: StoredImpersonationSession = {
      targetUser,
      originalAdminUser,
      expiresAt: now + IMPERSONATION_DURATION_MS,
      startedAt: now,
    };
    
    this.setStoredSession(session);
    return session;
  }

  /**
   * Get current impersonation state
   */
  static getCurrentState(): ImpersonationState {
    const session = this.getStoredSession();
    
    if (!session) {
      return { isImpersonating: false };
    }
    
    return {
      isImpersonating: true,
      targetUser: session.targetUser,
      originalAdminUser: session.originalAdminUser,
      expiresAt: session.expiresAt,
    };
  }

  /**
   * Get current impersonation user ID (for API headers)
   */
  static getCurrentImpersonationUserId(): string | null {
    const session = this.getStoredSession();
    return session?.targetUser?.id || null;
  }

  /**
   * Get current target user (for context)
   */
  static getCurrentTargetUser(): User | null {
    const session = this.getStoredSession();
    return session?.targetUser || null;
  }

  /**
   * Get original admin user
   */
  static getOriginalAdminUser(): User | null {
    const session = this.getStoredSession();
    return session?.originalAdminUser || null;
  }

  /**
   * Check if session will expire soon (within 5 minutes)
   */
  static isExpiringSoon(): boolean {
    const remainingTime = this.getRemainingTime();
    return remainingTime > 0 && remainingTime < (5 * 60 * 1000); // 5 minutes
  }

  /**
   * Get session duration constant
   */
  static getDurationMs(): number {
    return IMPERSONATION_DURATION_MS;
  }
}

// Export singleton instance for convenience
export const impersonationManager = ImpersonationManager;